(function (global, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  if (global && global.document) factory(global, global.document);
})(typeof window !== 'undefined' ? window : globalThis, function createVejiCookieBanner(win, doc) {
  'use strict';

  if (!win || !doc) return {};

  const root = doc.querySelector('[data-veji-cookie-banner]');
  const preferencesDialog = doc.querySelector('[data-veji-cookie-preferences-dialog]');
  const preferencesPanel = doc.querySelector('[data-veji-cookie-preferences-panel]');
  const preferenceTriggers = Array.from(doc.querySelectorAll('[data-veji-cookie-preferences]'));
  if (!root && !preferencesDialog && preferenceTriggers.length === 0) return {};

  const status = root && root.querySelector('[data-veji-cookie-status]');
  const actionButtons = root ? Array.from(root.querySelectorAll('[data-veji-cookie-action]')) : [];
  const preferenceActions = preferencesDialog
    ? Array.from(preferencesDialog.querySelectorAll('[data-veji-cookie-preferences-action]'))
    : [];
  const consentInputs = preferencesDialog
    ? Array.from(preferencesDialog.querySelectorAll('[data-veji-cookie-consent]'))
    : [];
  const preferencesStatus = preferencesDialog
    && preferencesDialog.querySelector('[data-veji-cookie-preferences-status]');
  const enabled = root && root.dataset.enabled === 'true';
  const designMode = root && root.dataset.designMode === 'true';
  const allControls = [...actionButtons, ...preferenceActions, ...consentInputs];
  let resizeObserver = null;
  let customerPrivacyPromise = null;
  let returnFocusTarget = null;

  const api = () => win.Shopify && win.Shopify.customerPrivacy;
  const setStatus = (target, message) => { if (target) target.textContent = message || ''; };
  const setBusy = (busy) => {
    allControls.forEach((control) => { control.disabled = busy; });
    if (preferencesPanel) preferencesPanel.setAttribute('aria-busy', busy ? 'true' : 'false');
  };

  function syncHeight() {
    if (!root || root.hidden) return;
    doc.body.style.setProperty('--veji-cookie-banner-height', `${Math.ceil(root.getBoundingClientRect().height)}px`);
  }

  function show() {
    if (!root) return;
    doc.body.classList.add('veji-cookie-banner-managed');
    if (!root.hidden) {
      syncHeight();
      return;
    }
    root.hidden = false;
    doc.body.classList.add('veji-cookie-banner-visible');
    syncHeight();
    if (win.ResizeObserver && !resizeObserver) {
      resizeObserver = new win.ResizeObserver(syncHeight);
      resizeObserver.observe(root);
    }
  }

  function hide() {
    if (!root) return;
    root.hidden = true;
    doc.body.classList.remove('veji-cookie-banner-visible');
    doc.body.style.removeProperty('--veji-cookie-banner-height');
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  }

  function loadCustomerPrivacy() {
    const customerPrivacy = api();
    if (customerPrivacy) return Promise.resolve(customerPrivacy);
    if (customerPrivacyPromise) return customerPrivacyPromise;
    if (!win.Shopify || typeof win.Shopify.loadFeatures !== 'function') {
      return Promise.reject(new Error('Shopify Customer Privacy indisponible'));
    }

    customerPrivacyPromise = new Promise((resolve, reject) => {
      try {
        win.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          (error) => {
            const loadedApi = api();
            if (error || !loadedApi) {
              reject(error || new Error('Shopify Customer Privacy indisponible'));
              return;
            }
            resolve(loadedApi);
          },
        );
      } catch (error) {
        reject(error);
      }
    });

    customerPrivacyPromise.catch(() => {
      customerPrivacyPromise = null;
    });
    return customerPrivacyPromise;
  }

  function prefillPreferences(customerPrivacy) {
    let consent = {};
    try {
      consent = customerPrivacy && customerPrivacy.currentVisitorConsent
        ? customerPrivacy.currentVisitorConsent()
        : {};
    } catch (error) {
      consent = {};
    }
    consentInputs.forEach((input) => {
      const value = consent[input.dataset.vejiCookieConsent];
      input.checked = value === 'yes' || value === true;
    });
  }

  function closePreferences({ restoreFocus = true } = {}) {
    if (!preferencesDialog || preferencesDialog.hidden) return;
    preferencesDialog.hidden = true;
    doc.body.classList.remove('veji-cookie-preferences-open');
    if (restoreFocus && returnFocusTarget && typeof returnFocusTarget.focus === 'function') {
      returnFocusTarget.focus();
    }
    returnFocusTarget = null;
  }

  function revealPreferences(trigger) {
    if (!preferencesDialog || !preferencesPanel) return false;
    returnFocusTarget = trigger || doc.activeElement || null;
    preferencesDialog.hidden = false;
    doc.body.classList.add('veji-cookie-preferences-open');
    if (typeof preferencesPanel.focus === 'function') preferencesPanel.focus();
    return true;
  }

  function openPreferences(trigger) {
    if (designMode || !revealPreferences(trigger)) return Promise.resolve(false);
    setStatus(status, '');
    setStatus(preferencesStatus, 'Chargement de vos préférences…');

    const customerPrivacy = api();
    if (customerPrivacy) {
      prefillPreferences(customerPrivacy);
      setStatus(preferencesStatus, '');
      return Promise.resolve(true);
    }

    return loadCustomerPrivacy().then((loadedApi) => {
      prefillPreferences(loadedApi);
      setStatus(preferencesStatus, '');
      return true;
    }, () => {
      setStatus(
        preferencesStatus,
        "Vos préférences ne peuvent pas être chargées pour le moment. Réessayez ou consultez notre politique de confidentialité.",
      );
      return false;
    });
  }

  function submit(consent, targetStatus) {
    setBusy(true);
    setStatus(status, '');
    setStatus(preferencesStatus, '');

    return loadCustomerPrivacy().then((customerPrivacy) => new Promise((resolve) => {
      try {
        customerPrivacy.setTrackingConsent(consent, (result) => {
          if (result && result.error) {
            setBusy(false);
            setStatus(targetStatus, "Votre choix n'a pas pu être enregistré. Réessayez.");
            resolve(false);
            return;
          }
          setBusy(false);
          hide();
          closePreferences();
          resolve(true);
        });
      } catch (error) {
        setBusy(false);
        setStatus(targetStatus, "Votre choix n'a pas pu être enregistré. Réessayez.");
        resolve(false);
      }
    }), () => {
      setBusy(false);
      setStatus(targetStatus, "Votre choix n'a pas pu être enregistré. Réessayez.");
      return false;
    });
  }

  function selectedConsent() {
    return consentInputs.reduce((consent, input) => {
      consent[input.dataset.vejiCookieConsent] = Boolean(input.checked);
      return consent;
    }, {});
  }

  function boot() {
    const customerPrivacy = api();
    if (!enabled || !root) return;
    if (designMode) {
      show();
      return;
    }
    if (!customerPrivacy) return;
    try {
      const consent = customerPrivacy.currentVisitorConsent
        ? customerPrivacy.currentVisitorConsent()
        : {};
      const hasExplicitChoice = ['analytics', 'marketing', 'preferences'].some((key) => (
        consent[key] === 'yes' || consent[key] === 'no'
      ));
      if (customerPrivacy.shouldShowBanner() && !hasExplicitChoice) show();
      else hide();
    } catch (error) {
      return;
    }
  }

  function ensureCustomerPrivacy() {
    if (designMode) {
      boot();
      return Promise.resolve(api());
    }
    return loadCustomerPrivacy().then((customerPrivacy) => {
      boot();
      return customerPrivacy;
    }, () => null);
  }

  for (const button of actionButtons) {
    button.addEventListener('click', (event) => {
      const action = button.dataset.vejiCookieAction;
      if (action === 'choose') {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
      }
      if (designMode) return;
      if (action === 'accept') submit(
        { analytics: true, marketing: true, preferences: true },
        status,
      );
      if (action === 'choose') openPreferences(button);
    });
  }

  for (const trigger of preferenceTriggers) {
    trigger.addEventListener('click', () => {
      if (designMode) return;
      openPreferences(trigger);
    });
  }

  for (const button of preferenceActions) {
    button.addEventListener('click', () => {
      if (designMode) return;
      const action = button.dataset.vejiCookiePreferencesAction;
      if (action === 'close') closePreferences();
      if (action === 'reject') submit(
        { analytics: false, marketing: false, preferences: false },
        preferencesStatus,
      );
      if (action === 'save') submit(selectedConsent(), preferencesStatus);
      if (action === 'accept') submit(
        { analytics: true, marketing: true, preferences: true },
        preferencesStatus,
      );
    });
  }

  if (preferencesDialog) {
    preferencesDialog.addEventListener('keydown', (event) => {
      if (!event || preferencesDialog.hidden) return;
      if (event.key === 'Escape') {
        if (typeof event.preventDefault === 'function') event.preventDefault();
        closePreferences();
        return;
      }
      if (event.key !== 'Tab' || !preferencesPanel) return;

      const focusable = Array.from(preferencesPanel.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), a[href]',
      ));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && event.target === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && event.target === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  doc.addEventListener('shopifyCustomerPrivacyApiLoaded', boot, { once: true });
  doc.addEventListener('visitorConsentCollected', () => {
    hide();
    closePreferences({ restoreFocus: false });
  });
  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', () => { ensureCustomerPrivacy(); }, { once: true });
  } else {
    ensureCustomerPrivacy();
  }

  return {
    boot,
    ensureCustomerPrivacy,
    show,
    hide,
    submit,
    openPreferences,
    closePreferences,
    selectedConsent,
  };
});
